<template>
  <div class="bdc">
    <div class="bdc-head">
      <span class="bdc-head__bar" />
      <span class="bdc-head__title">{{ meta.title }}</span>
      <span class="bdc-head__total">共 {{ total }} 项</span>
    </div>

    <div v-if="chartType !== 'location'" class="bdc-body">
      <div class="bdc-donut" :style="{background: donutGradient}">
        <div class="bdc-donut__hole">
          <div class="bdc-donut__num">{{ total }}</div>
          <div class="bdc-donut__lbl">项目</div>
        </div>
      </div>
      <div class="bdc-legend">
        <div v-for="(item, i) in items" :key="item.name" class="bdc-legend__row">
          <span class="bdc-legend__dot" :style="{background: colorOf(i)}" />
          <span class="bdc-legend__name">{{ item.name }}</span>
          <span class="bdc-legend__bar-wrap">
            <span class="bdc-legend__bar" :style="{width: pct(item.value) + '%', background: colorOf(i)}" />
          </span>
          <span class="bdc-legend__val">{{ item.value }}</span>
        </div>
      </div>
    </div>

    <div v-else class="bdc-bars">
      <div v-for="(item, i) in items" :key="item.name" class="bdc-bar">
        <span class="bdc-bar__label">{{ item.name }}</span>
        <div class="bdc-bar__track">
          <div class="bdc-bar__fill" :style="{width: barWidth(item.value), background: colorOf(i)}" />
        </div>
        <span class="bdc-bar__val">{{ item.value }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import {USE_MOCK, mockCharts} from './bid-dashboard-mock';
import {CHART_COLORS, chartColor} from './bid-dashboard-theme';

const META = {
  scale: {title: '规模统计'},
  location: {title: '位置分布'},
  type: {title: '类型分布'},
};

export default {
  props: {
    data: {type: Object, default: () => ({})},
  },
  data() {
    return {items: []};
  },
  computed: {
    chartType() {
      return this.data.chart || 'scale';
    },
    meta() {
      return META[this.chartType] || META.scale;
    },
    total() {
      return this.items.reduce((s, v) => s + v.value, 0);
    },
    donutGradient() {
      if (!this.total) return '#d6e8fb';
      let acc = 0;
      const stops = this.items.map((item, i) => {
        const start = acc;
        acc += (item.value / this.total) * 100;
        return `${chartColor(i)} ${start}% ${acc}%`;
      });
      return `conic-gradient(${stops.join(', ')})`;
    },
    maxValue() {
      return Math.max(...this.items.map((v) => v.value), 1);
    },
  },
  created() {
    this.loadData();
  },
  methods: {
    loadData() {
      if (USE_MOCK) {
        this.items = mockCharts(this.chartType);
        return;
      }
      this.$api.get('/business/bid/dashboard/charts', {type: this.chartType}).then((res) => {
        this.items = res.data.data || [];
      }).catch(() => {
        this.items = mockCharts(this.chartType);
      });
    },
    colorOf(i) {
      return chartColor(i);
    },
    pct(val) {
      return this.total ? Math.round(val / this.total * 100) : 0;
    },
    barWidth(val) {
      return `${Math.round(val / this.maxValue * 100)}%`;
    },
  },
};
</script>

<style lang="less" scoped>
.bdc {
  height: 100%;
  padding: 8px 10px;
  background: #fff;
  border-radius: 8px;
  border: 1px solid #c5ddf5;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.bdc-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.bdc-head__bar {
  width: 3px;
  height: 14px;
  background: #1a6fd4;
  border-radius: 2px;
}

.bdc-head__title {
  font-size: 13px;
  font-weight: 700;
  color: #1e3a5f;
  flex: 1;
}

.bdc-head__total {
  font-size: 11px;
  color: #6b8aad;
}

.bdc-body {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 0;
}

.bdc-donut {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.bdc-donut__hole {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.bdc-donut__num {
  font-size: 18px;
  font-weight: 800;
  color: #0d4a8f;
  line-height: 1;
}

.bdc-donut__lbl {
  font-size: 9px;
  color: #6b8aad;
}

.bdc-legend {
  flex: 1;
  min-width: 0;
  overflow: hidden;
}

.bdc-legend__row {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 2px 0;
  font-size: 11px;
}

.bdc-legend__dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.bdc-legend__name {
  width: 68px;
  flex-shrink: 0;
  color: #6b8aad;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.bdc-legend__bar-wrap {
  flex: 1;
  height: 4px;
  background: #eef4fc;
  border-radius: 2px;
  overflow: hidden;
}

.bdc-legend__bar {
  display: block;
  height: 100%;
  border-radius: 2px;
}

.bdc-legend__val {
  width: 18px;
  text-align: right;
  font-weight: 700;
  color: #0d4a8f;
}

.bdc-bars {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-around;
  gap: 4px;
  min-height: 0;
}

.bdc-bar {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
}

.bdc-bar__label {
  width: 44px;
  flex-shrink: 0;
  color: #6b8aad;
  text-align: right;
}

.bdc-bar__track {
  flex: 1;
  height: 8px;
  background: #eef4fc;
  border-radius: 4px;
  overflow: hidden;
}

.bdc-bar__fill {
  height: 100%;
  border-radius: 4px;
}

.bdc-bar__val {
  width: 20px;
  text-align: right;
  font-weight: 700;
  color: #0d4a8f;
}
</style>
